import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import type { StepProgressFlowConfig, StepProgressIndicatorStep } from '@hunting-nature/shared-core/step-progress';
import { generateProgressSteps, getPositionFromUrl, getVisibleSteps } from '@hunting-nature/shared-core/step-progress';

@Component({
  selector: 'lib-progress-indicator',
  templateUrl: './progress-indicator.component.html',
  styleUrl: './progress-indicator.component.scss',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressIndicatorComponent {
  private readonly router = inject(Router);

  flowConfig = input.required<StepProgressFlowConfig>();

  // *Optional; When provided, it overrides the URL-derived
  // step — allowing the indicator to be used outside of routed flows (e.g. inside
  // a modal wizard where the step is tracked in component/store state).

  activeStep = input<number | undefined>(undefined);

  private readonly currentUrl$$ = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly totalSteps = computed(() => getVisibleSteps(this.flowConfig()));
  readonly currentStep = computed(() => this.activeStep() ?? getPositionFromUrl(this.flowConfig(), this.currentUrl$$()));
  readonly steps = computed<ReadonlyArray<StepProgressIndicatorStep>>(() => {
    return generateProgressSteps(this.flowConfig(), this.currentStep());
  });

  readonly ariaLabel = computed(() => `Step ${this.currentStep()} of ${this.totalSteps()}`);
}
